import { onReady } from '../../shared/utils/helpers';
import Base from '../../components/base/base';
import CookieConsent from '../../components/cookie-consent/cookie-consent';
import Header from '../../components/header/header';
import BuyerIntakeForm from '../../components/buyer-intake-form/buyer-intake-form';
import DesignConfiguratorBeta from '../../components/design-configurator/design-configurator-beta';
import LeadCaptureModal from '../../components/lead-capture-modal/lead-capture-modal';
import CombinatorPanel from '../../components/combinator-panel/combinator-panel';
import CombinatorModes from '../../components/combinator-panel/combinator-modes';
import CardCarousel from '../../components/card-carousel/card-carousel';
import {
  Recipe,
  buildConfig,
  collisions,
  planSelectRecipe,
  roofLayer,
  selectRecipe,
  sidingLayer,
  signatureRecipe,
  withOutputs
} from '../../components/combinator-panel/factory';
import { Catalog, fetchCatalog } from '../../shared/utils/elevation-catalog';
import './our-homes.scss';

interface OurHomesPageOptions {
  debug?: boolean;
  [key: string]: any;
}

class OurHomesPage extends Base {
  private header: Header;
  private buyerIntakeForm: BuyerIntakeForm;
  private designConfigurator: DesignConfiguratorBeta;
  private leadCaptureModal: LeadCaptureModal | null = null;
  private panels: CombinatorPanel[] = [];
  private modes: CombinatorModes[] = [];

  constructor(options: OurHomesPageOptions = {}) {
    super(options.debug || false);
    this.debug = options.debug || false;
    this.log('OurHomesPage constructor options:', options);

    this.header = new Header({ debug: this.debug });
    this.buyerIntakeForm = new BuyerIntakeForm({ debug: this.debug });
    this.designConfigurator = new DesignConfiguratorBeta({
      debug: this.debug,
      rootSelector: '[data-design-configurator-group]'
    });
    this.leadCaptureModal = new LeadCaptureModal({
      debug: this.debug,
      userType: 'interest-list',
      triggerSelector: '.interest-list-trigger .button'
    });

    this.initCombinators();
    this.log('OurHomesPage initialized');

    // Cookie consent gate. Webflow holds the Meta Pixel until this grants.
    try {
      new CookieConsent({ debug: this.debug });
      this.log('CookieConsent initialized');
    } catch (e) {
      this.log('CookieConsent init failed or skipped', e);
    }
  }

  /**
   * One catalog request feeds every panel on the page: the Signature and
   * Select pair under the toggle, and the lab versions below.
   */
  private async initCombinators(): Promise<void> {
    const catalog = await fetchCatalog({ debug: this.debug });

    if (!catalog) {
      this.log('CombinatorPanel: no catalog, the page keeps its markup');
      return;
    }

    const copy = withOutputs({ catalog });

    this.modes = Array.from(document.querySelectorAll<HTMLElement>('[data-cmb-modes]')).map(
      (root) => new CombinatorModes({ root, debug: this.debug })
    );

    this.start(catalog, '#signature-config[data-cmb="signature"]', signatureRecipe(copy));
    this.start(catalog, '#select-config[data-cmb="select"]', selectRecipe(copy));

    try {
      new CardCarousel({ catalog, debug: this.debug });
    } catch (e) {
      this.log('CardCarousel init failed or skipped', e);
    }
  }

  /** Start one panel when its root and at least one outcome exist. */
  private start(catalog: Catalog, selector: string, recipe: Recipe, initial?: Record<string, string>): void {
    const root = document.querySelector<HTMLElement>(selector);

    if (!root) return;

    const config = buildConfig(catalog, recipe);

    if (!config.outcomes.length) {
      this.log('CombinatorPanel: no outcomes for', selector);
      return;
    }

    if (this.debug) {
      const shared = collisions(catalog, recipe);

      if (shared.length) this.log('CombinatorPanel: combinations two elevations share', selector, shared);
    }

    this.panels.push(new CombinatorPanel({ root, config, initial, debug: this.debug }));
  }

  destroy(): void {
    this.panels.forEach((panel) => panel.destroy());
    this.modes.forEach((modes) => modes.destroy());
    this.designConfigurator.destroy();
  }
}

onReady(() => {
  new OurHomesPage({ debug: true });
});

export default OurHomesPage;
